'use client';
import type { ReactNode, ForwardedRef, ReactElement } from 'react';
import { useCallback, useState, forwardRef, useEffect } from 'react';
import { t } from 'i18next';

// shadcn
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@r/components/ui/command.tsx';
import { Popover, PopoverContent, PopoverTrigger } from '@r/components/ui/popover.tsx';

// utils
import { cn } from '@r/utils/utils.ts';

// assets
import { ChevronDown, CheckIcon, Globe, Loader2 } from 'lucide-react';

// Generic option interface
export interface SelectOption<T = unknown> {
  value: string;
  label: string;
  data?: T;
}

// Dropdown props
interface SelectDropdownProps<T = unknown> {
  options?: SelectOption<T>[];
  loading: boolean;
  onChange?: (option: SelectOption<T> | null) => void;
  defaultValue?: string;
  disabled?: boolean;
  placeholder?: string;
  slim?: boolean;
  searchPlaceholder?: string;
  renderOption?: (option: SelectOption<T>) => ReactNode;
  renderTrigger?: (selectedOption: SelectOption<T> | undefined) => ReactNode;
  allowDeselect?: boolean;
}

const SelectDropdownComponent = <T = unknown>(
  {
    options = [],
    onChange,
    defaultValue,
    loading = false,
    disabled = false,
    placeholder = 'Select an option',
    slim = false,
    searchPlaceholder,
    renderOption,
    renderTrigger,
    allowDeselect = false,
    ...props
  }: SelectDropdownProps<T>,
  ref: ForwardedRef<HTMLButtonElement>
) => {
  const [open, setOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<SelectOption<T> | undefined>(undefined);

  useEffect(() => {
    if (defaultValue && options.length > 0) {
      const initialOption = options.find((option) => option.value === defaultValue);
      setSelectedOption(initialOption || undefined);
    } else {
      setSelectedOption(undefined);
    }
  }, [defaultValue, options]);

  const handleSelect = useCallback(
    (option: SelectOption<T> | null) => {
      setSelectedOption(option || undefined);
      onChange?.(option);
      setOpen(false);
    },
    [onChange]
  );

  const triggerClasses = cn(
    'flex h-[2.5rem] w-full items-center justify-between whitespace-nowrap rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1',
    slim === true && 'w-20'
  );

  const isDisabled = loading || disabled;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger ref={ref} className={triggerClasses} disabled={isDisabled} {...props}>
        {loading ? (
          <div className="flex gap-2 items-center">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="ml-2">{slim === false ? t('common.loading') : ''}</span>
          </div>
        ) : selectedOption ? (
          <div className="flex overflow-hidden flex-grow gap-2 items-center w-0">
            {renderTrigger
              ? renderTrigger(selectedOption)
              : slim === false && (
                  <span className="overflow-hidden whitespace-nowrap text-ellipsis">
                    {selectedOption.label}
                  </span>
                )}
          </div>
        ) : (
          <span>{slim === false ? placeholder : <Globe size={20} />}</span>
        )}
        <ChevronDown size={16} />
      </PopoverTrigger>
      <PopoverContent
        collisionPadding={10}
        side="bottom"
        className="min-w-[--radix-popper-anchor-width] p-0"
      >
        <Command className="w-full max-h-[200px] sm:max-h-[270px]">
          <CommandList>
            <div className="sticky top-0 z-10 bg-popover">
              <CommandInput
                placeholder={
                  searchPlaceholder || t('common.label.search.by', { field: placeholder })
                }
                disabled={loading}
              />
            </div>
            {loading ? (
              <div className="flex justify-center items-center p-4">
                <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                <span>{t('common.loading')}</span>
              </div>
            ) : (
              <CommandGroup>
                {options.map((option, key: number) => (
                  <CommandItem
                    className="flex gap-2 items-center w-full cursor-pointer"
                    key={key}
                    onSelect={() => {
                      if (allowDeselect && selectedOption?.value === option.value) {
                        handleSelect(null);
                      } else {
                        handleSelect(option);
                      }
                    }}
                  >
                    <div className="flex overflow-hidden flex-grow space-x-2 w-0">
                      {renderOption ? (
                        renderOption(option)
                      ) : (
                        <span className="overflow-hidden whitespace-nowrap text-ellipsis">
                          {option.label}
                        </span>
                      )}
                    </div>
                    <CheckIcon
                      className={cn(
                        'ml-auto h-4 w-4 shrink-0',
                        option.value === selectedOption?.value ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

SelectDropdownComponent.displayName = 'SelectDropdownComponent';

export const SelectDropdown = forwardRef(SelectDropdownComponent) as <T = unknown>(
  props: SelectDropdownProps<T> & { ref?: ForwardedRef<HTMLButtonElement> }
) => ReactElement;
